/**
 * Today's next-step wording and its context-sensitive selection (AIC-334, agreed with Codex on AIC-346), shared by the
 * analyst portal (screens/Today.tsx, which shows it) and tracker-api (routes/today-paged.ts, whose server-side search
 * `q` matches it), so what the desk shows and what the search matches can't drift. Pure and dependency-free: the
 * portal bundles it. No React, no server-only imports, no authorization: which actions a row offers stays the UI's
 * and the API's own business; this only chooses the words.
 *
 * The wording is Today's as of AIC-346 (0723559), with the approved 2026-09-30 prototype's row copy for spec ready,
 * spec approved, sent and results ready. One state's wording depends on context: spec_approved ("Create the invite
 * draft" with its Outlook-connected or connect-first line, vs "Invite draft not created yet"), chosen by the
 * drafting context (todayDrafting): the caller's own queue, not in an admin's preview, and whether their Outlook is
 * connected. A status the catalogue doesn't know falls back to its next_action (else its status), underscores as
 * spaces, with no secondary text.
 *
 * The in-flight "Creating the invite draft…" override (TODAY_PENDING_STEP) is transient UI feedback while a draft is
 * being created, not a durable state: the server never searches it.
 *
 * Search (todayMatches): a case-insensitive substring of the concatenated text, the query trimmed; empty or
 * whitespace-only is no filter. Assignments search "company label secondary" (so a phrase can span the company/label
 * boundary); review items "company reason", and since AIC-372 also "company title", "company issue_label" and
 * "company summary", each matched on its own (todayReviewMatches). Lowercasing is JavaScript's toLowerCase on both sides, with no Unicode
 * normalization, in the portal and the server alike. The query is never a pattern: %, _ and * are plain characters.
 */

/** Today's row states (the portal's status catalogue keys). */
export type TodayState =
  | "synced" | "specReady" | "specOk" | "drafted" | "sent" | "sched" | "interviewed" | "results" | "follow" | "approved"
  | "exported" | "withdrawn";

/**
 * tracker-api's statuses and derived display statuses → Today's states, as the portal's components/status.tsx maps
 * them. A row's state is its display_status's (else its status's); null for a status the catalogue doesn't know.
 */
export const TODAY_STATE_OF: Readonly<Record<string, TodayState>> = {
  assigned: "synced", spec_ready: "specReady", spec_approved: "specOk", invite_drafted: "drafted", sent: "sent",
  interview_scheduled: "sched", interviewed: "interviewed", results_ready: "results", needs_follow_up: "follow",
  approved: "approved", exported: "exported", withdrawn: "withdrawn",
};

export function todayStateOf(status: string): TodayState | null {
  return Object.hasOwn(TODAY_STATE_OF, status) ? TODAY_STATE_OF[status]! : null;
}

/**
 * Who can create a spec-approved row's invite draft, from the viewer's side:
 *   yes      the caller's own queue, not a preview, Outlook connected: they create it now
 *   outlook  the caller's own queue, not a preview, Outlook not connected: connect it first
 *   reconnect the caller's own queue, not a preview, Outlook connected but Microsoft no longer accepts the sign-in
 *   unsure   the caller's own queue, not a preview, the connection can't be told (still checking, status failed to
 *            load, not set up, account not mapped): never "Connect Outlook", which may be wrong (AIC-521)
 *   no       an admin's analyst preview, or another analyst's or a team's queue: the owning analyst creates it
 */
export type TodayDrafting = "yes" | "outlook" | "reconnect" | "unsure" | "no";

/**
 * The caller's Outlook as the portal sees it (its /me/graph load): connected, not connected, connected but needing a
 * fresh sign-in (reconnect), or not known (still checking, failed to load, not set up, unmapped).
 */
export type TodayOutlook = "connected" | "disconnected" | "reconnect" | "unknown";
export const TODAY_OUTLOOK: readonly TodayOutlook[] = ["connected", "disconnected", "reconnect", "unknown"];

/**
 * The drafting context. ownQueue: the caller's own queue (the portal: no analyst picked and not a team scope; the
 * API: no analyst_id). Only "connected" is ready. Each other state gets its own wording (AIC-521): the copy says
 * Connect only when the portal knows Outlook is off, Reconnect when it needs a new sign-in, and asks to check the
 * connection when it can't tell, so a connected analyst is never told to connect.
 */
export function todayDrafting(c: { readonly preview: boolean; readonly ownQueue: boolean; readonly outlook: TodayOutlook }): TodayDrafting {
  if (c.preview || !c.ownQueue) return "no";
  switch (c.outlook) {
    case "connected": return "yes";
    case "disconnected": return "outlook";
    case "reconnect": return "reconnect";
    default: return "unsure";
  }
}

/** [primary, secondary]: the next step, in the drafts-only voice (never "Send" as an action). */
export type TodayStep = readonly [string, string];

/** Every state's wording but spec_approved's, which depends on the drafting context (TODAY_SPEC_APPROVED_STEP). */
export const TODAY_STEP: Readonly<Record<Exclude<TodayState, "specOk">, TodayStep>> = {
  synced: ["Interview spec being prepared", "From IDC's estimates"],
  specReady: ["Approve the interview spec", "Check the figures and questions before preparing the vendor's invitation."],
  drafted: ["Send the invite from your Outlook", "The draft is in your Drafts — you send it"],
  sent: ["Waiting for the vendor", "The interview link is with the vendor. No action is needed from you."],
  sched: ["Interview scheduled", "Waiting for the call"],
  interviewed: ["Verifier checking the transcript", "Results appear when it's done"],
  results: ["Review interview results", "Compare the completed interview with IDC's estimates."],
  follow: ["Follow up with the vendor", "Some figures need another answer"],
  approved: ["Writing back to the Data Layer", "Results approved"],
  exported: ["Exported", "The results are in the Data Layer"],
  withdrawn: ["Withdrawn", "No further steps"],
};

export const TODAY_SPEC_APPROVED_STEP: Readonly<Record<TodayDrafting, TodayStep>> = {
  yes: ["Create the invite draft", "The approved interview is ready. Create a draft, then send it from Outlook."],
  outlook: ["Create the invite draft", "Connect Outlook to create the draft, then send it from Outlook."],
  reconnect: ["Create the invite draft", "Reconnect Outlook to create the draft, then send it from Outlook."],
  unsure: ["Create the invite draft", "Check your Outlook connection to create the draft, then send it from Outlook."],
  no: ["Invite draft not created yet", "The owning analyst creates it"],
};

/** The portal's transient override while it creates a row's draft (UI feedback only; never searched by the server). */
export const TODAY_PENDING_STEP: TodayStep = ["Create the invite draft", "Creating the invite draft…"];

/** The next step for a row in state `state`; `fallback` (next_action, else status) for a state the catalogue doesn't know. */
export function todayStep(state: TodayState | null, drafting: TodayDrafting, fallback: string): TodayStep {
  if (state === null) return [fallback.replace(/_/g, " "), ""];
  return state === "specOk" ? TODAY_SPEC_APPROVED_STEP[drafting] : TODAY_STEP[state];
}

/** The next step for an API row: its state from display_status (else status), falling back to next_action (else status). */
export function todayStepOf(row: { readonly status: string; readonly display_status?: string | null; readonly next_action?: string | null }, drafting: TodayDrafting): TodayStep {
  return todayStep(todayStateOf(row.display_status ?? row.status), drafting, row.next_action ?? row.status);
}

/**
 * AIC-433: an invite drafted this many days ago and still not detected as sent is flagged on Today, with a hint to
 * Mark as sent (sent detection can miss a send whose tracking header was dropped, or a draft copied into a new mail).
 */
export const SEND_UNDETECTED_DAYS = 3;

/** Whole days since the row's invite draft was created, when it is invite_drafted and at least SEND_UNDETECTED_DAYS old; else null. */
export function sendUndetectedDays(row: { readonly status: string; readonly draft_created_at?: string | null }, now: Date = new Date()): number | null {
  if (row.status !== "invite_drafted" || !row.draft_created_at) return null;
  const at = Date.parse(row.draft_created_at);
  if (Number.isNaN(at)) return null;
  const days = Math.floor((now.getTime() - at) / 86_400_000);
  return days >= SEND_UNDETECTED_DAYS ? days : null;
}

/** The longest search the API accepts, in characters (code points) after trimming. The portal enforces it; nothing truncates. */
export const TODAY_SEARCH_MAX = 100;

/** The search as applied: trimmed, null when empty (no filter). */
export function todaySearch(q: string | null | undefined): string | null {
  const t = (q ?? "").trim();
  return t ? t : null;
}

/** The text an assignment's search matches: "company primary secondary". */
export const todayAssignmentText = (company: string, step: TodayStep): string => `${company} ${step.join(" ")}`;
/** The text a review item's search matches: "company reason". */
export const todayReviewText = (company: string, reason: string): string => `${company} ${reason}`;

/** A review item as search reads it: its company and reason, and its short copy (AIC-372) when it has one. */
export interface TodayReviewSearchable {
  readonly company: string; readonly reason: string;
  readonly title?: string | null; readonly summary?: string | null; readonly issue_label?: string | null;
}

/**
 * AIC-372: every text a review item's search matches: "company reason" (as before, unchanged), then "company title",
 * "company issue_label" and "company summary" for the copy it has. Each is matched on its own, so a query matches
 * whatever it matched before, and a phrase can span the company and any one of them, never two of them.
 */
export function todayReviewTexts(r: TodayReviewSearchable): string[] {
  return [todayReviewText(r.company, r.reason), ...[r.title, r.issue_label, r.summary].filter((x): x is string => typeof x === "string" && x !== "").map((x) => `${r.company} ${x}`)];
}

/** Whether a review item matches the search (todayReviewTexts, each by todayMatches); no query matches everything. */
export const todayReviewMatches = (r: TodayReviewSearchable, q: string | null | undefined): boolean => todayReviewTexts(r).some((t) => todayMatches(t, q));

/** Whether `text` matches the search: a case-insensitive substring, the query trimmed; no query matches everything. */
export function todayMatches(text: string, q: string | null | undefined): boolean {
  return text.toLowerCase().includes((q ?? "").trim().toLowerCase());
}
